// Keyboard/on-screen simulator: injects synthetic MIDI-shaped events into the same pipeline.
export const SIM_CONFIG = {
  tickMs: 130,
  strokeNoteEveryTicks: 3,
  strokeVelocity: [20, 48],
  strokeCcCeiling: 58,
  strokeCcStep: [1.5, 3.5],
  hitVelocity: [108, 127],
  hitCcPeak: 118,
  hitDecayStep: 14,
  releaseDecayStep: 8,
  channel: 1,
  flutterBase: 20,
  strokeLevel: 62,
  tapLevel: 88,
  tapTicks: 4,
  hoverLevel: 18,
  hoverSlew: 5,
  phantomEveryTicks: 9, // phantom TAP every ~1.2 s
  stuckHoverCycleTicks: 68,
  stuckHoverOnTicks: 58,
};

const rand = ([a, b]) => a + Math.random() * (b - a);

export function createSimulator(emit, { now = () => performance.now(), config = SIM_CONFIG } = {}) {
  const listeners = new Set();
  let timer = null;
  let tickN = 0;
  let cc = 0;
  let strokeLevel = 0;
  let pendingOff = [];
  let flutter = 0;
  let tapTicks = 0;
  let pressTicks = 0;
  const st = { enabled: false, stroking: false, rough: false, crowd: false, hovering: false, hits: 0, phantomTap: false, stuckTouch: false, stuckHover: false };

  const notify = () => listeners.forEach((fn) => fn({ ...st }));
  const send = (ev) => emit({ cc: null, value: null, note: null, velocity: null, channel: config.channel, src: 'sim', ...ev, t: now() });
  const sendCc = (v) => {
    cc = Math.max(0, Math.min(127, Math.round(v)));
    send({ type: 'cc', cc: 90, value: cc });
  };

  let stepOffset = 0;
  let lvl = 45;
  // Mirrors the real Biotron: one CC90 + one note per 130 ms clock; CC rests on a plateau with slow drift
  // and small ±7 steps. A touch (hold W or Q) slews CC ~35/tick to a far plateau that holds; release slews back.
  function step() {
    tickN += 1;
    pendingOff.forEach((note) => send({ type: 'note_off', note, velocity: 0 }));
    pendingOff = [];
    if (st.phantomTap && tickN % config.phantomEveryTicks === 0) tapTicks = 3; // faulty electronics: a TAP like clockwork
    const touching = st.stroking || st.rough || st.crowd || st.stuckTouch || tapTicks > 0 || pressTicks > 0;
    const hovering = st.hovering || (st.stuckHover && tickN % config.stuckHoverCycleTicks < config.stuckHoverOnTicks); // a flaky sensor: near-signal on ~7.5 s, off ~1.3 s
    // plateau steps happen with no hand near; a hand nearby holds the plateau
    if (!touching && !hovering && !st.stuckHover && Math.random() < 1 / 15) stepOffset = Math.max(-14, Math.min(14, stepOffset + (Math.random() < 0.5 ? -7 : 7)));
    const rest = 45 + 8 * Math.sin((tickN * config.tickMs) / 120000 * 2 * Math.PI) + stepOffset;
    if (tapTicks > 0) tapTicks -= 1;
    if (pressTicks > 0) pressTicks -= 1;
    // hover (hold H): a hand near moves CC a smaller, slower way (+18, ~5/sample) than contact (+65, ~25/sample)
    const target = touching ? Math.min(120, rest + 65) : hovering ? rest + config.hoverLevel : rest;
    const slew = !touching && hovering && lvl < target ? config.hoverSlew : 25;
    lvl += Math.max(-slew, Math.min(slew, target - lvl)); // real CC90 slides ~25-30/sample over 0.3-0.5 s
    const v = Math.max(0, Math.min(127, Math.round(lvl + (touching ? (Math.random() - 0.5) * 4 : hovering ? (Math.random() - 0.5) * 2 : 0))));
    sendCc(v);
    const note = 48 + Math.round((v * 36) / 127);
    send({ type: 'note_on', note, velocity: Math.round(rand([15, 90])) });
    pendingOff.push(note);
  }
  function setMode(key, on) {
    if (!st.enabled || st[key] === !!on) return;
    st[key] = !!on;
    notify();
  }

  function setEnabled(on) {
    st.enabled = !!on;
    if (st.enabled && !timer) timer = setInterval(step, config.tickMs);
    if (!st.enabled) {
      st.stroking = false;
      st.hovering = false;
      st.rough = false;
      st.crowd = false;
      st.phantomTap = false;
      st.stuckTouch = false;
      st.stuckHover = false;
      if (timer) clearInterval(timer);
      timer = null;
      if (cc > 0) sendCc(0);
    }
    notify();
  }

  function hit() {
    if (!st.enabled) return;
    st.hits += 1;
    tapTicks = 3;
    notify();
  }

  function setStroke(on) {
    if (!st.enabled || st.stroking === !!on) return;
    st.stroking = !!on;
    if (on) pressTicks = 1; // even a very quick press moves the signal for one sample
    notify();
  }

  return {
    setEnabled,
    toggle: () => setEnabled(!st.enabled),
    hit,
    setStroke,
    setHover: (on) => setMode('hovering', on),
    setRough: (on) => setMode('rough', on),
    setCrowd: (on) => setMode('crowd', on),
    // glitch simulator (faulty-word safeguard tests): phantomTap | stuckTouch | stuckHover
    setGlitch(kind, on) {
      if (on && !st.enabled) setEnabled(true);
      setMode(kind, on);
    },
    getState: () => ({ ...st }),
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}

const isTyping = (el) => el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

export function bindSimulatorKeys(sim, target = window) {
  // W or Q held = touching; how long you hold decides TAP vs STROKE. H held = hand hovering near (TEASE).
  const held = new Set();
  const down = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target)) return;
    const k = e.key.toLowerCase();
    if (k === 's' && !e.repeat) sim.toggle();
    if (k === 'w' || k === 'q') {
      held.add(k);
      sim.setStroke(true);
    }
  };
  const up = (e) => {
    const k = e.key.toLowerCase();
    if (k === 'w' || k === 'q') {
      held.delete(k);
      if (!held.size) sim.setStroke(false);
    }
  };
  const blur = () => {
    held.clear();
    sim.setStroke(false);
    sim.setHover(false);
  };
  target.addEventListener('keydown', down);
  target.addEventListener('keyup', up);
  target.addEventListener('blur', blur);
  return () => {
    target.removeEventListener('keydown', down);
    target.removeEventListener('keyup', up);
    target.removeEventListener('blur', blur);
  };
}
